import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  Search,
  UserPlus,
  UserRoundCheck,
  CalendarCheck2,
  Tent,
  ArrowRightCircle,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';

import {
  useFetchEventLeads,
  useClaimEventLead,
  useMarkEventLead,
} from '../../../services/onboardingBoard';
import { fullLeadName, formatPhoneToken } from './boardUtils';

// Events bucket (re-homed to the BOARD 2026-08-24, Nic; was a Lead Handoffs
// overlay): NEW leads that arrived through event partners wait here, next to
// (and left of) the Backlog. Claim puts one on YOUR Day 1 in Making Contact and runs the
// 10-day cadence, exactly like a backlog claim. Transfer to Lead Handoffs
// exists ONLY for leads that already have a meeting booked (server-enforced
// too): an unbooked transfer is exactly the never-worked-lead mess the
// 2026-08-24 cleanup removed from Handoffs.

const arrivedLabel = (d) =>
  new Date(d).toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

const meetingLabel = (iso) =>
  new Date(iso).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

const EventRow = ({ lead, busy, onClaim, onTransfer }) => (
  <div className="rounded-xl bg-white px-3.5 py-2.5 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_8px_rgba(15,23,42,0.05)] dark:!bg-surface-card dark:ring-1 dark:ring-white/10">
    <div className="flex items-center gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100">
            {fullLeadName(lead)}
          </span>
          {lead.booking ? (
            // Past ACTIVE bookings are un-actioned meetings: nobody
            // canceled or marked no-show, so whether it happened is a
            // human call — amber "met ... (unconfirmed)", never a green
            // "meets" for a date already gone (Nic 2026-08-24).
            new Date(lead.booking.startTime) > new Date() ? (
              <span className="flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-1.5 py-px text-[10px] font-semibold text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400">
                <CalendarCheck2 size={10} />
                meets {meetingLabel(lead.booking.startTime)}
              </span>
            ) : (
              <span className="flex shrink-0 items-center gap-1 rounded-full bg-amber-50 px-1.5 py-px text-[10px] font-semibold text-amber-600 dark:bg-amber-500/15 dark:text-amber-400">
                <CalendarCheck2 size={10} />
                met {meetingLabel(lead.booking.startTime)} (unconfirmed)
              </span>
            )
          ) : null}
          {/* Existing account (2026-08-25, visibility-only v1): this person
              already signed up on their own. Do not dial them cold; ping
              Nic to link the records. */}
          {lead.existingAccount ? (
            <span
              title={`Existing account: ${lead.existingAccount.name} (${
                lead.existingAccount.userType === 'pending_client'
                  ? 'pending client'
                  : 'client'
              }), matched by ${lead.existingAccount.matchedBy}. This person likely signed up on their own. Ping Nic to review and link.`}
              className="flex shrink-0 items-center gap-1 rounded-full bg-amber-50 px-1.5 py-px text-[10px] font-semibold text-amber-600 dark:bg-amber-500/15 dark:text-amber-400"
            >
              <UserRoundCheck size={10} />
              Existing Account
            </span>
          ) : null}
        </div>
        <div className="mt-0.5 flex min-w-0 items-center gap-2 whitespace-nowrap text-xs text-slate-400 dark:text-slate-500">
          <span className="shrink-0">
            arrived {arrivedLabel(lead.leadCreatedOn)}
          </span>
          {lead.leadPhone ? (
            <>
              <span className="shrink-0">·</span>
              <span className="shrink-0">
                {formatPhoneToken(lead.leadPhone)}
              </span>
            </>
          ) : null}
          {lead.notes ? (
            <>
              <span className="shrink-0">·</span>
              <span className="truncate" title={lead.notes}>
                {lead.notes}
              </span>
            </>
          ) : null}
        </div>
      </div>
      <button
        type="button"
        disabled={busy}
        onClick={onClaim}
        title="Add them to your Day 1 in Making Contact and start the 10-day cadence"
        className="flex shrink-0 items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-500 disabled:opacity-50"
      >
        <UserPlus size={13} /> Claim
      </button>
    </div>
    {lead.booking ? (
      // The heads-up state (Nic 2026-08-24): they already booked a meeting,
      // so the cadence has nothing to chase: they go straight onto your
      // Signing Up, meeting booked. Only rendered (and only accepted
      // server-side) with an active booking.
      <button
        type="button"
        disabled={busy}
        onClick={onTransfer}
        className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg bg-violet-600/10 px-3 py-1.5 text-[11px] font-semibold text-violet-700 hover:bg-violet-600/20 disabled:opacity-50 dark:bg-violet-500/15 dark:text-violet-300 dark:hover:bg-violet-500/25"
      >
        <ArrowRightCircle size={12} />
        Meeting already booked, put them on my Signing Up
      </button>
    ) : null}
  </div>
);

const EventsPanel = ({ onClose }) => {
  const queryClient = useQueryClient();
  const list = useFetchEventLeads({});
  const claimMut = useClaimEventLead();
  const markMut = useMarkEventLead();
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState(null);

  const leads = useMemo(() => {
    const all = list.data?.leads || [];
    const q = search.trim().toLowerCase();
    if (!q) return all;
    const digits = q.replace(/\D/g, '');
    return all.filter((l) => {
      const name = fullLeadName(l).toLowerCase();
      const email = String(l.leadEmail || '').toLowerCase();
      const phone = String(l.leadPhone || '').replace(/\D/g, '');
      return (
        name.includes(q) ||
        email.includes(q) ||
        (digits.length >= 3 && phone.includes(digits))
      );
    });
  }, [list.data, search]);

  // Grouped by event, biggest first (same read the old overlay gave).
  const groups = useMemo(() => {
    const byEvent = new Map();
    for (const lead of leads) {
      const key = lead.eventName || 'Unknown event';
      if (!byEvent.has(key)) byEvent.set(key, []);
      byEvent.get(key).push(lead);
    }
    return [...byEvent.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [leads]);

  const refresh = () => {
    queryClient.invalidateQueries(['event-leads']);
    queryClient.invalidateQueries(['onboarding-board']);
  };

  const claim = async (lead) => {
    setBusyId(lead.leadId);
    try {
      await claimMut.mutateAsync({ leadId: lead.leadId });
      toast.success(
        `${fullLeadName(lead)} is on your Day 1 in Making Contact.`,
      );
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.msg || 'Could not claim this lead.');
      refresh();
    } finally {
      setBusyId(null);
    }
  };

  const transfer = async (lead) => {
    setBusyId(lead.leadId);
    try {
      await markMut.mutateAsync({ leadId: lead.leadId, mark: 'signing_up' });
      toast.success(`${fullLeadName(lead)} is on your Signing Up now.`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.msg || 'That didn’t go through.');
      refresh();
    } finally {
      setBusyId(null);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[1060]">
      <div
        className="absolute inset-0 bg-slate-900/25 dark:bg-black/50"
        onClick={onClose}
        aria-hidden
      />
      <div className="absolute bottom-0 right-0 top-0 flex w-full max-w-md flex-col bg-[#F5F7FA] shadow-2xl dark:bg-surface-page">
        <div className="bg-white px-4 py-3 shadow-[0_1px_3px_rgba(15,23,42,0.06)] dark:!bg-surface-elevated">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="flex items-center gap-2 text-base font-bold text-slate-800 dark:text-slate-100">
                <Tent size={16} className="text-slate-400" />
                Events
                {leads.length > 0 ? (
                  <span className="rounded-full bg-slate-900 px-1.5 py-px text-[10px] font-semibold text-white dark:bg-slate-200 dark:!text-slate-900">
                    {leads.length}
                  </span>
                ) : null}
              </div>
              <div className="mt-0.5 text-xs text-slate-400 dark:text-slate-500">
                Claim to add to Day 1 in Making Contact.
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-white/10 dark:hover:text-slate-200"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
          <div className="relative mt-2.5">
            <Search
              size={14}
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-300"
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, phone, email"
              className="w-full rounded-full bg-[#F5F7FA] py-1.5 pl-8 pr-3 text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-300 dark:bg-white/5 dark:text-slate-200 dark:placeholder:text-slate-500"
            />
          </div>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto p-3">
          {list.isLoading ? (
            <div className="py-10 text-center text-xs text-slate-400">
              Loading event leads…
            </div>
          ) : leads.length === 0 ? (
            <div className="py-10 text-center text-xs text-slate-400">
              {search.trim()
                ? 'No event lead matches that search.'
                : 'No event leads waiting. Nice.'}
            </div>
          ) : (
            groups.map(([eventName, eventLeads]) => (
              <div key={eventName}>
                <div className="mb-1.5 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                  {eventName}
                  <span className="rounded-full bg-slate-100 px-1.5 py-px text-[10px] font-semibold text-slate-400 dark:bg-white/10">
                    {eventLeads.length}
                  </span>
                </div>
                <div className="space-y-2">
                  {eventLeads.map((lead) => (
                    <EventRow
                      key={lead.leadId}
                      lead={lead}
                      busy={busyId === lead.leadId}
                      onClaim={() => claim(lead)}
                      onTransfer={() => transfer(lead)}
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default EventsPanel;
